import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
} from '@mui/material';
import { type RuleNotifications } from 'types/notification.types';
import StyledSwitchButton from 'components/Buttons/SwitchButton';
import { Notification } from './CellRenderer.styled';
import { StatusWrapper } from 'components/AgGrid/CellRenderer/CellRenderer';
import { formatConditions } from 'views/Settings/Rules/helpers/common';
import { COLORS, NOTIFICATION_LEVEL } from 'utils/enums';
import { useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
import { ThemePalette } from 'mui.theme';
import { StatusDot } from 'components/StatusIndicator/IgnitionStatusIndicator';
import { isAssetIgnition } from 'views/FleetOverview/helpers/asset.helpers';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

interface Props {
  value: string;
  data: RuleNotifications;
  handleNameClick: (
    ruleId: string,
    deviceId: string,
    assetVin: string,
    alertStartTs: string,
    isRead: boolean,
    conditions: any
  ) => void;
  selectedNotification: any;
  handleToggleClick: (
    notificationId: string,
    isActive: boolean,
    rule: { ruleName: string },
    asset: { bumperNumber: string },
    note: string
  ) => void;
  latestTagData: any;
  asset: any;
}

export const NotificationDate = ({ data: { alertStartTs } }: Props) => {
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  return (
    <span>
      {convertUtcToDisplayTime(
        alertStartTs,
        preferredTimeDisplayMode,
        timezone,
        DATE_TIME_FORMAT
      )}
    </span>
  );
};

export const NotificationTagName = ({
  data: {
    notificationId,
    deviceId,
    isRead,
    alertStartTs,
    conditions,
    asset: { assetVin },
    rule: { ruleName },
  },
  handleNameClick,
}: Props) => {
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme = isDarkTheme(theme);
  const { selectedNotification } = useAppSelector(
    (state) => state.ruleNotificationsReducer
  );
  return (
    <Notification
      isRead={isRead}
      onClick={() => {
        if (selectedNotification?.notificationId === notificationId) {
          return null;
        } else {
          handleNameClick(
            notificationId,
            deviceId,
            assetVin,
            alertStartTs,
            isRead,
            conditions
          );
        }
      }}
    >
      <span
        style={{
          color: (enableDarkTheme ? _palette.dark : _palette.light).textPrimary,
          textDecoration: 'none',
        }}
      >
        {' '}
        {ruleName}
      </span>
    </Notification>
  );
};

export const NotificationSeverity = ({
  data: {
    rule: { notificationLevel },
  },
}: Props) => {
  let notificationLevelString = NOTIFICATION_LEVEL.find(
    (item) => item.id === notificationLevel
  )?.display;
  let textColor = 'black';
  if (notificationLevelString) {
    textColor = getColorBasedOnString(notificationLevelString);
    const words = notificationLevelString.split(' ');
    notificationLevelString = words[0];
  }
  return (
    <span
      style={{
        color: textColor,
      }}
    >
      {notificationLevelString}
    </span>
  );
};

export const Status = ({ data: { isActive } }: Props) => {
  return <StatusWrapper isActive={isActive} />;
};

export const NotificationAsset = ({
  data: {
    asset: { bumperNumber },
  },
  latestTagData,
  asset,
}: Props) => {
  return (
    <>
      <StatusDot isActive={isAssetIgnition(latestTagData, asset)} />
      <span>{bumperNumber}</span>
    </>
  );
};

export const LastConnected = ({ data: { alertEndTs } }: Props) => {
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  return (
    <span>
      {alertEndTs
        ? convertUtcToDisplayTime(
            alertEndTs,
            preferredTimeDisplayMode,
            timezone,
            'MM/DD/YYYY h:mma (Z)'
          )
        : ''}
    </span>
  );
};

export const RulesLogic = ({ data: { conditions } }: Props) => {
  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;

  return (
    <span>{conditions ? formatConditions(conditions, unitSystem) : ''}</span>
  );
};

export const Delete = ({
  data: {
    notificationId,
    isActive,
    rule: { ruleName },
    asset: { bumperNumber },
  },
  handleToggleClick,
}: Props) => {
  const { t } = useTranslation();
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme = isDarkTheme(theme);
  const [open, setOpen] = useState(false);
  const [userNote, setUserNote] = useState('');

  const handleConfirm = () => {
    handleToggleClick(
      notificationId,
      !isActive,
      { ruleName },
      { bumperNumber },
      userNote
    );

    setUserNote('');
    setOpen(false);
  };

  return (
    <>
      {isActive && (
        <FormControlLabel
          control={<StyledSwitchButton />}
          label=""
          labelPlacement="start"
          checked={isActive}
          sx={{ textAlign: 'center', marginRight: '10px' }}
          onChange={() => {
            setOpen(true);
          }}
        />
      )}

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('confirm_action')}</DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <TextField
            label={t('add_a_note_optional')}
            multiline
            minRows={3}
            fullWidth
            value={userNote}
            onChange={(e) => {
              setUserNote(e.target.value);
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: enableDarkTheme
                  ? ThemePalette?.dark?.cartesianGrid
                  : ThemePalette?.light?.menuOptions,
                color: enableDarkTheme
                  ? ThemePalette?.typography?.white
                  : ThemePalette?.typography?.black,
                '& fieldset': {
                  borderColor: enableDarkTheme
                    ? _palette.dark.borderGrey
                    : '#E0E0E0',
                },
                '&:hover fieldset': {
                  borderColor: enableDarkTheme
                    ? _palette.dark.primary
                    : _palette.light.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: enableDarkTheme
                    ? _palette.dark.primary
                    : _palette.light.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: enableDarkTheme
                  ? ThemePalette?.typography?.white
                  : ThemePalette?.typography?.black,
              },
              '& .MuiInputBase-input::placeholder': {
                color: enableDarkTheme
                  ? ThemePalette?.typography?.lightGrey
                  : ThemePalette?.typography?.secondary,
                opacity: 1,
              },
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
            }}
          >
            {t('cancel')}
          </Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const TooltipComponent = ({ value }: { value: string }) => {
  return (
    <div
      className="tooltip"
      style={{
        backgroundColor: '#D3D3D3',
        padding: '10px',
      }}
    >
      {value}
    </div>
  );
};

export const RulesLogicToolTip = ({ value }: { value: any }) => {
  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;
  return (
    <div
      className="tooltip"
      style={{
        backgroundColor: '#D3D3D3',
        padding: '10px',
      }}
    >
      {value ? formatConditions(value, unitSystem) : ''}
    </div>
  );
};

function getColorBasedOnString(str: string) {
  const lowerStr = str.toLowerCase();
  if (lowerStr.includes('low')) {
    return COLORS.grey;
  } else if (lowerStr.includes('medium')) {
    return COLORS.yellow;
  } else if (lowerStr.includes('high')) {
    return COLORS.red;
  } else {
    // You can choose a default color if the input string doesn't include any of the expected values
    return 'black';
  }
}
